/*!
 * Copyright (c) 2017-Present, Okta, Inc. and/or its affiliates. All rights reserved.
 * The Okta software accompanied by this notice is provided pursuant to the Apache License, Version 2.0 (the "License.")
 *
 * You may obtain a copy of the License at http://www.apache.org/licenses/LICENSE-2.0.
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS, WITHOUT
 * WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 *
 * See the License for the specific language governing permissions and limitations under the License.
 */

// copied from https://github.com/okta/okta-react/blob/master/samples/routing/react-router-dom-v5-hash/src/components/LoginCallback.tsx
import React from 'react';

import { faLoader } from '@awesome.me/kit-f3dc60f0d0/icons/classic/regular';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Trans } from '@lingui/react/macro';
import { useOktaAuth } from '@okta/okta-react';
import { useHistory } from 'react-router-dom';

import { loginChannel, useLogin } from '../modules/login/useLogin';

const FALLBACK_TIMEOUT_MS = 5000;

const LoginCallback: React.FC<{
  homePath: string;
}> = ({ homePath }) => {
  const { oktaAuth } = useOktaAuth();
  const history = useHistory();
  const { success } = useLogin();
  const [showFallback, setShowFallback] = React.useState(false);

  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      setShowFallback(true);
    }, FALLBACK_TIMEOUT_MS);

    return () => clearTimeout(timeoutId);
  }, []);

  React.useEffect(() => {
    const handleLoginRedirect = async () => {
      await oktaAuth.handleLoginRedirect();
    };

    if (oktaAuth.token.isLoginRedirect()) {
      handleLoginRedirect()
        .then(() => {
          const accessToken = oktaAuth.getAccessToken();
          return accessToken
            ? success(accessToken).then((didNavigate) => {
                loginChannel.postMessage('login');
                if (!didNavigate) {
                  // success() has no matching navigation condition (e.g. normal
                  // single-practice user), so explicitly leave /login/callback.
                  // restoreOriginalUri operates on the hash history and cannot
                  // navigate the BrowserRouter away from this route.
                  history.replace(homePath);
                }
              })
            : null;
        })
        .catch((e) => {
          // TODO: handle error state(s)
          console.error(e);
        });
    } else {
      // we landed on root path, but do not have login callback params in url query string
      history.replace(homePath);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook React.useEffect has a missing dependency: 'success'. Either include it or remove the dependency array.
  }, [oktaAuth, history, homePath]);

  return (
    <div className="vai-flex vai-flex-col vai-items-center vai-justify-center vai-h-screen vai-bg-gray-50 vai-text-gray-700">
      <FontAwesomeIcon
        icon={faLoader}
        className="vai-h-8 vai-w-8 vai-text-indigo-500 vai-animate-spin"
      />
      <p className="vai-mt-5 vai-text-base vai-font-medium">
        <Trans>Signing you in...</Trans>
      </p>
      {showFallback && (
        <p className="vai-mt-3 vai-text-sm vai-text-gray-500">
          <Trans>If you are not redirected,</Trans>{' '}
          <button
            type="button"
            onClick={() => history.replace(homePath)}
            className="vai-text-indigo-500 vai-underline vai-cursor-pointer vai-bg-transparent vai-border-0 vai-p-0 vai-font-[inherit] vai-text-[inherit]"
          >
            <Trans>click here</Trans>
          </button>{' '}
          <Trans>to try again.</Trans>
        </p>
      )}
    </div>
  );
};

export default LoginCallback;
